// Geodesic (real lat/lng) math for the Aerial drawing surface, ported from
// JMI-GeoSketch-Standalone's src/lib/geo.ts, plus local feet<->lat/lng
// conversion helpers used to reconcile Aerial-drawn shapes with the flat
// feet-grid Tape tool through a shared origin anchor (TapeSketch.OriginLat/Lng).

const R_FT = 20902230.97;

function toRad(d) {
  return (d * Math.PI) / 180;
}
function toDeg(r) {
  return (r * 180) / Math.PI;
}

export function wholeFt(n) {
  if (!Number.isFinite(n)) {
    return 0;
  }
  const r = Math.round(n);
  if (r === 0 && Math.abs(n) >= 0.5) {
    return n > 0 ? 1 : -1;
  }
  return r;
}

export function destPoint(from, bearingDegrees, feet) {
  const delta = feet / R_FT;
  const theta = toRad(bearingDegrees);
  const phi1 = toRad(from.lat);
  const lambda1 = toRad(from.lng);
  const phi2 = Math.asin(Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(theta));
  const lambda2 =
    lambda1 + Math.atan2(Math.sin(theta) * Math.sin(delta) * Math.cos(phi1), Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2));
  return { lat: toDeg(phi2), lng: ((toDeg(lambda2) + 540) % 360) - 180 };
}

export function distanceFt(a, b) {
  const phi1 = toRad(a.lat);
  const phi2 = toRad(b.lat);
  const dPhi = toRad(b.lat - a.lat);
  const dLambda = toRad(b.lng - a.lng);
  const s = Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return 2 * R_FT * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

export function bearingDeg(a, b) {
  const phi1 = toRad(a.lat);
  const phi2 = toRad(b.lat);
  const dLambda = toRad(b.lng - a.lng);
  const y = Math.sin(dLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

export function cardinalSnap(from, to) {
  const d = wholeFt(distanceFt(from, to));
  if (d < 1) {
    return from;
  }
  const br = bearingDeg(from, to);
  const cardinal = [0, 90, 180, 270].reduce((best, c) => {
    const diff = Math.min(Math.abs(br - c), 360 - Math.abs(br - c));
    const bdiff = Math.min(Math.abs(br - best), 360 - Math.abs(br - best));
    return diff < bdiff ? c : best;
  }, 0);
  return destPoint(from, cardinal, d);
}

export function bearingOfDir(d) {
  return d === 'N' ? 0 : d === 'E' ? 90 : d === 'S' ? 180 : 270;
}

export function rectFromOrigin(origin, widthFt, depthFt) {
  const w = Math.max(1, wholeFt(widthFt));
  const d = Math.max(1, wholeFt(depthFt));
  const e = destPoint(origin, 90, w);
  const n = destPoint(origin, 0, d);
  const ne = destPoint(e, 0, d);
  return [origin, e, ne, n];
}

// ---- Local feet-frame <-> lat/lng, anchored at a shared origin ----
// Equirectangular approximation around the origin's latitude — exact enough
// at building-footprint scale (matches the standalone's own aerial sqft/GLA math).

function scaleFactors(originLat) {
  const lat0 = (originLat * Math.PI) / 180;
  const ftPerDegLat = 110574 * 3.28084;
  const ftPerDegLng = 111320 * Math.cos(lat0) * 3.28084;
  return { ftPerDegLat, ftPerDegLng };
}

export function latLngToFeet(origin, latlng) {
  const { ftPerDegLat, ftPerDegLng } = scaleFactors(origin.lat);
  return {
    x: (latlng.lng - origin.lng) * ftPerDegLng,
    y: (latlng.lat - origin.lat) * ftPerDegLat,
  };
}

export function feetToLatLng(origin, pt) {
  const { ftPerDegLat, ftPerDegLng } = scaleFactors(origin.lat);
  return {
    lat: origin.lat + pt.y / ftPerDegLat,
    lng: origin.lng + pt.x / ftPerDegLng,
  };
}
